/**
 * DSD → IXD 정방향 매칭 맵.
 *
 * DSD 셀을 선택했을 때 매칭되는 IXD 셀들을 강조(포커싱)하기 위해 필요하다.
 * `DsdParsedCell.matchedIxdCells` 가 DSD → IXD 방향을 직접 제공하므로 이를 dsdId 기준으로 모은다.
 *
 * IXD 셀 검색/강조(searchByCellIds, useIxdCellReviewStyles)는 원본 cellId(`data-cell-id`)로
 * 하므로 정규화하지 않은 원본 IXD cellId 를 그대로 담는다. cellId 는 축만 다른 물리
 * 셀끼리 충돌할 수 있어, 매치 ref 에 metadata 가 있으면 identityKey 를 함께 담는다 —
 * 소비처가 `data-cell-identity-key` 기반으로 올바른 물리 셀만 강조/스크롤한다.
 */

import { makeCellIdentityKey } from "@/utils/ixd-cell-model";
import type { DSDSubChunkInfo } from "@/apis/compare/route";
import type { MostProperCell } from "@/apis/compare/types";

export interface IxdMatchRef {
  cellId: string;
  /** 매칭 fact 의 identity key. 구버전 레코드(metadata 없음)는 null. */
  identityKey: string | null;
  /**
   * 이 DSD 셀의 정본 매칭인가. 스크롤 목적지를 고르는 데 쓴다 — 셀 색 판정도 이 셀만
   * 보므로 색과 스크롤이 어긋나지 않는다.
   */
  isMostProper: boolean;
}

export interface DsdMatchRefs {
  refs: IxdMatchRef[];
  /**
   * 백엔드가 고른 정본 셀. 스크롤 후보를 좁히는 기준이라 **평탄화하지 않고 그대로**
   * 넘긴다 — 소비자가 "지목이 없다"와 "지목은 있는데 그 축을 모른다"를 구분해야
   * 하고, 둘의 폴백 규칙이 다르다(`useDsdIxdCounterpart` 참고).
   */
  proper: MostProperCell | null;
}

/** dsdId → 매칭되는 IXD 셀 참조 목록(원본 cellId + identityKey, 중복 제거) + 정본 셀 */
export const buildIxdByDsdCellIdMap = (
  subChunks: DSDSubChunkInfo[] | undefined | null,
): Map<string, DsdMatchRefs> => {
  const map = new Map<string, DsdMatchRefs>();
  if (!subChunks?.length) return map;

  for (const subChunk of subChunks) {
    for (const block of subChunk.parsedContent ?? []) {
      if (block.blockType !== "table" || !block.cells) continue;
      for (const row of block.cells) {
        for (const cell of row) {
          if (!cell || !cell.isOrigin) continue;
          const seen = new Set<string>();
          const refs: IxdMatchRef[] = [];
          const proper = cell.mostProperCell;
          for (const matched of cell.matchedIxdCells ?? []) {
            if (!matched?.cellId) continue;
            const identityKey = matched.metadata
              ? makeCellIdentityKey(matched.metadata)
              : null;
            const dedupKey = `${matched.cellId} ${identityKey ?? ""}`;
            if (seen.has(dedupKey)) continue;
            seen.add(dedupKey);
            refs.push({
              cellId: matched.cellId,
              identityKey,
              isMostProper: matched.cellId === proper?.cellId,
            });
          }
          if (refs.length > 0) {
            map.set(cell.dsdId, { refs, proper: proper ?? null });
          }
        }
      }
    }
  }
  return map;
};

/**
 * 이 서브청크들에서 어떤 DSD 값과든 매칭된 IXD 원본 cellId 전체 집합.
 *
 * 표 셀뿐 아니라 문단의 `sentenceMatches` 도 함께 훑는다 — 여기서 빠진 IXD 셀은
 * 소비처에서 "매칭 DSD 없음"(빨강)으로 단언되므로, 문장에서 매칭된 값을 빼면
 * 매칭이 있는데도 없다고 잘못 표시된다.
 */
export const collectMatchedIxdCellIds = (
  subChunks: DSDSubChunkInfo[] | undefined | null,
): string[] => {
  const ids = new Set<string>();
  for (const subChunk of subChunks ?? []) {
    for (const block of subChunk.parsedContent ?? []) {
      const matchSources = [
        ...(block.cells ?? []).flat(),
        ...(block.sentenceMatches ?? []),
      ];
      for (const source of matchSources) {
        for (const matched of source?.matchedIxdCells ?? []) {
          if (matched?.cellId) ids.add(matched.cellId);
        }
      }
    }
  }
  return Array.from(ids);
};

/**
 * IxdMatchRef 목록을 강조용으로 분리한다: identityKey 가 있는 ref 는 identity
 * 선택자로(축 충돌 구분), 없는 ref 는 기존 cellId 선택자로 강조한다.
 */
export const splitRefsForCounterpart = (
  refs: Iterable<Pick<IxdMatchRef, "cellId" | "identityKey">>,
): { cellIds: string[]; identityKeys: string[] } => {
  const cellIds = new Set<string>();
  const identityKeys = new Set<string>();
  for (const ref of refs) {
    if (ref.identityKey) identityKeys.add(ref.identityKey);
    else cellIds.add(ref.cellId);
  }
  return {
    cellIds: Array.from(cellIds),
    identityKeys: Array.from(identityKeys),
  };
};
